import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { useRolePerms } from '../../features/auth/useRolePerms.js';
import { toast } from '../../lib/toast.js';

interface Note {
  id: string;
  text: string;
  date: string;
  authorName: string | null;
}

interface Worker { id: string; name: string; role: string; notes: Note[]; }

interface Props { onClose: () => void; workerId: string; }

export function WorkerNotesModal({ onClose, workerId }: Props) {
  const { isAdmin } = useRolePerms();
  const qc = useQueryClient();
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: w } = useQuery({ queryKey: ['workers', workerId], queryFn: () => api.get<Worker>(`/workers/${workerId}`).then((r) => r.data) });

  const add = useMutation({
    mutationFn: async () => api.post(`/workers/${workerId}/notes`, { text: newNote.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workers', workerId] });
      qc.invalidateQueries({ queryKey: ['workers'] });
      setNewNote('');
      toast.success('Note added');
    },
    onError: () => toast.error('Save failed'),
  });

  if (!w) return null;

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 600 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title" id="wn-title">📝 Worker Notes</div>
        <div className="modal-sub" id="wn-sub">{w.name} · {w.role}</div>
        <div id="wn-list" style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 14 }}>
          {w.notes.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 12, textAlign: 'center', padding: 16 }}>No notes yet.</div>
          ) : w.notes.map((n) => (
            <div key={n.id} style={{ padding: '10px 12px', marginBottom: 7, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8 }}>
              <div style={{ fontSize: 12, lineHeight: 1.4 }}>{n.text}</div>
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 5 }}>
                {new Date(n.date).toLocaleString()}{n.authorName ? ` · ${n.authorName}` : ''}
              </div>
            </div>
          ))}
        </div>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 13 }}>
          <div className="sec-title" style={{ marginBottom: 8 }}>Add New Note</div>
          {isAdmin ? <><div className="form-row"><textarea className="form-textarea" id="wn-new" placeholder="Type note here…" value={newNote} onChange={(e) => setNewNote(e.target.value)} style={{ minHeight: 80 }} /></div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary btn-sm" onClick={() => add.mutate()} disabled={add.isPending || !newNote.trim()}>{add.isPending ? 'Saving…' : '+ Add Note'}</button>
          </div></> : null}
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
